import planClient from '@/api/plans.js'
import orderClient from '@/api/orders.js'
import subscriptionClient from '@/api/subscriptions.js'
import planChangeRequestsClient from '@/api/planChangeRequests.js'
import { logShowPlanSelectModal } from '@/utils/segment.js'

// initial state
const state = {
  inProgressSubscription: null, // Newly created and may or may not be 'incomplete'.
  currentSubscription: null, // Subscription that is active and in use by a team/user.
  loadingSubscription: false,
  plans: [],
  selectedPlan: null,
  trackToDownload: null,
  showDownloadModal: false,
  showPlanSelectionModal: false,
  createSubscriptionWithTrial: false,
  signupButtonLabelOverride: null,
  upgradeablePlans: [],
  selectedUpgradePlan: null
}

// getters
const getters = {
  inProgressSubscription: (state) => state.inProgressSubscription,

  currentSubscription: (state) => state.currentSubscription,

  plans: (state) => state.plans,

  selectedPlan: (state) => state.selectedPlan,

  loadingSubscription: (state) => state.loadingSubscription,

  showDownloadModal: (state) => state.showDownloadModal,

  showPlanSelectionModal: (state) => state.showPlanSelectionModal,

  trackToDownload: (state) => state.trackToDownload,

  createSubscriptionWithTrial: (state) => state.createSubscriptionWithTrial,

  signupButtonLabel: (state) => {
    // This is used in multiple pages so adding here rather than repeating in
    // each page.
    if (state.signupButtonLabelOverride !== null) {
      return state.signupButtonLabelOverride
    }
    if (state.createSubscriptionWithTrial) {
      return 'Start 30-day free trial'
    }
    return 'Sign up for free'
  },

  upgradeablePlans: (state) => state.upgradeablePlans,

  selectedUpgradePlan: (state) => state.selectedUpgradePlan
}

// actions
const actions = {
  fetchCurrentSubscription: (context) => {
    context.commit(SET_LOADING_STATE, true)
    return subscriptionClient.retrieveCurrent()
      .then((response) => {
        context.dispatch('setCurrentSubscription', response.data)
        context.commit(SET_LOADING_STATE, false)
      })
  },

  cancelCurrentSubscription: (context) => {
    const currentSubscription = context.getters.currentSubscription
    return subscriptionClient.cancel(currentSubscription.pk)
      .then((response) => {
        context.dispatch('setCurrentSubscription', response.data)
      })
  },

  reactivateCurrentSubscription: (context) => {
    const currentSubscription = context.getters.currentSubscription
    return subscriptionClient.reactivate(currentSubscription.pk)
      .then((response) => {
        context.dispatch('setCurrentSubscription', response.data)
      })
  },

  setCurrentSubscription: (context, subscription) => {
    context.commit(SET_CURRENT_SUBSCRIPTION, subscription)
    if (subscription.payment_failed && subscription.payment_failure_message) {
      const message = `<p>There looks to be a problem with you subscription payment. <strong>${subscription.payment_failure_message}</strong> Please check the subscription tab in your account profile.</p>`
      const toastMessage = {
        message,
        position: 'top-center',
        type: 'is-danger',
        duration: 10000,
        dismissible: true,
        closeOnClick: false,
        pauseOnHover: true
      }
      context.dispatch('common/showToastMessage', toastMessage, { root: true })
    }

    const daysBeforeThreshold = 7
    const activeStatuses = ['trialing', 'active']
    if (subscription.cancel_at_period_end === true && activeStatuses.indexOf(subscription.status) > -1) {
      const subscriptionDaysBeforeEnd = subscription.current_period_length - subscription.day_of_current_period

      if (subscriptionDaysBeforeEnd > 0 && subscriptionDaysBeforeEnd <= daysBeforeThreshold) {
        const message = 'Your subscription is set to cancel soon and you will lose access to our great music. Visit the subscription page to reactivate your subscription.'
        const toastMessage = {
          message,
          position: 'top-center',
          type: 'is-warning',
          duration: 10000,
          dismissible: true,
          closeOnClick: false,
          pauseOnHover: true
        }
        context.dispatch('common/showToastMessage', toastMessage, { root: true })
      }
    }
  },

  fetchPlans: (context) => {
    return planClient.listAll()
      .then((plans) => {
        context.commit(SET_PLANS, plans)
      })
  },

  fetchUpgradeOptions: (context) => {
    return planClient.upgradeOptionsAll()
      .then((plans) => {
        context.commit(SET_UPGRADEABLE_PLANS, plans)
      })
      .catch((error) => {
        // 404 means no active subscription (or upgrades switched off): nothing to offer.
        if (error.response && error.response.status === 404) {
          context.commit(SET_UPGRADEABLE_PLANS, [])
          return
        }
        return Promise.reject(error)
      })
  },

  selectUpgradePlan: (context, item) => {
    context.commit(SELECT_UPGRADE_PLAN, item)
  },

  createPlanChangeRequest: (context, planPk) => {
    return planChangeRequestsClient.create({ target_plan: planPk })
      .then(() => {
        context.commit(SET_UPGRADEABLE_PLANS, [])
        context.commit(SELECT_UPGRADE_PLAN, null)
      })
  },

  selectPlan: (context, item) => {
    context.commit(SELECT_PLAN, item)
  },

  setTrackToDownload: (context, track) => {
    context.commit(SET_TRACK_TO_DOWNLOAD, track)
  },

  showDownloadModal: (context) => {
    context.commit(SET_SHOW_DOWNLOAD_MODAL, true)
  },

  hideDownloadModal: (context) => {
    context.commit(SET_SHOW_DOWNLOAD_MODAL, false)
  },

  showPlanSelectionModal: (context) => {
    context.commit(SET_SHOW_PLAN_SELECTION_MODAL, true)
    logShowPlanSelectModal()
  },

  hidePlanSelectionModal: (context) => {
    context.commit(SET_SHOW_PLAN_SELECTION_MODAL, false)
  },

  setInProgressSubscription: (context, subscription) => {
    context.commit(SET_IN_PROGRESS_SUBSCRIPTION, subscription)
  },

  setCreateSubscriptionWithTrial: (context, value) => {
    context.commit(SET_CREATE_SUBSCRIPTION_WITH_TRIAL, value)
  },

  doSubscriptionOrder: (context) => {
    const track = context.getters.trackToDownload
    const currentSubscription = context.getters.currentSubscription
    const orderData = {
      subscription: currentSubscription.pk,
      items: [{ track: track.pk, product: currentSubscription.plan.licensing_product }]
    }
    return orderClient.create(orderData)
      .then((response) => {
        // The download licenses the track, so refresh which tracks lists mark as licensed.
        context.dispatch('licenses/fetchLicensesLite', null, { root: true })
        return response
      })
  },

  clearCurrentObjects: (context) => {
    context.commit(SET_IN_PROGRESS_SUBSCRIPTION, null)
    context.commit(SET_CURRENT_SUBSCRIPTION, null)
  },

  setSignupButtonLabelOverride: (context, value) => {
    context.commit(SET_SIGNUP_BUTTON_LABEL_OVERRIDE, value)
  },

  clearSignupButtonLabelOverride: (context) => {
    context.commit(SET_SIGNUP_BUTTON_LABEL_OVERRIDE, null)
  }
}

// mutations
const SET_IN_PROGRESS_SUBSCRIPTION = 'SET_IN_PROGRESS_SUBSCRIPTION'
const SET_CURRENT_SUBSCRIPTION = 'SET_CURRENT_SUBSCRIPTION'
const SET_PLANS = 'SET_PLANS'
const SELECT_PLAN = 'SELECT_PLAN'
const SET_LOADING_STATE = 'SET_LOADING_STATE'
const SET_SHOW_DOWNLOAD_MODAL = 'SET_SHOW_DOWNLOAD_MODAL'
const SET_SHOW_PLAN_SELECTION_MODAL = 'SET_SHOW_PLAN_SELECTION_MODAL'
const SET_TRACK_TO_DOWNLOAD = 'SET_TRACK_TO_DOWNLOAD'
const SET_CREATE_SUBSCRIPTION_WITH_TRIAL = 'SET_CREATE_SUBSCRIPTION_WITH_TRIAL'
const SET_SIGNUP_BUTTON_LABEL_OVERRIDE = 'SET_SIGNUP_BUTTON_LABEL_OVERRIDE'
const SET_UPGRADEABLE_PLANS = 'SET_UPGRADEABLE_PLANS'
const SELECT_UPGRADE_PLAN = 'SELECT_UPGRADE_PLAN'

const mutations = {
  [SET_IN_PROGRESS_SUBSCRIPTION]: (state, subscription) => {
    state.inProgressSubscription = subscription
  },

  [SET_CURRENT_SUBSCRIPTION]: (state, subscription) => {
    state.currentSubscription = subscription
  },

  [SET_PLANS]: (state, plans) => {
    state.plans = plans
  },

  [SELECT_PLAN]: (state, item) => {
    state.selectedPlan = item
  },

  [SET_LOADING_STATE]: (state, value) => {
    state.loadingSubscription = value
  },

  [SET_SHOW_DOWNLOAD_MODAL]: (state, value) => {
    state.showDownloadModal = value
  },

  [SET_SHOW_PLAN_SELECTION_MODAL]: (state, value) => {
    state.showPlanSelectionModal = value
  },

  [SET_TRACK_TO_DOWNLOAD]: (state, value) => {
    state.trackToDownload = value
  },

  [SET_CREATE_SUBSCRIPTION_WITH_TRIAL]: (state, value) => {
    state.createSubscriptionWithTrial = value
  },

  [SET_SIGNUP_BUTTON_LABEL_OVERRIDE]: (state, value) => {
    state.signupButtonLabelOverride = value
  },

  [SET_UPGRADEABLE_PLANS]: (state, plans) => {
    state.upgradeablePlans = plans
  },

  [SELECT_UPGRADE_PLAN]: (state, item) => {
    state.selectedUpgradePlan = item
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
